import React from 'react';

import { useTrack } from 'src/contexts/AnalyticsContext';
import { Events } from 'src/lib/trackingEvents';

/**
 * `<input>` types holding text the visitor types themselves. Checkboxes, the
 * GDPR radios and the submit button all reach the same delegated listener, so
 * the event has to narrow down to the fields it actually reports on.
 */
const TEXT_INPUT_TYPES = new Set([
  'email',
  'number',
  'password',
  'search',
  'tel',
  'text',
  'url',
]);

/**
 * The field name for a focused text input, or `null` when focus landed on
 * anything else.
 *
 * Names identify the field rather than DOM ids because `TextInput` and
 * `TextArea` both suffix their ids with a `useId` value, which is not stable
 * across renders and so cannot be grouped on downstream. Names also already
 * identify fields in `FORM_SUBMIT_ERROR`'s `errorFields`.
 */
const getFocusedTextInputName = (target: EventTarget | null): string | null => {
  const element = target as HTMLInputElement | HTMLTextAreaElement | null;

  const isTextInput =
    element?.tagName === 'TEXTAREA' ||
    (element?.tagName === 'INPUT' &&
      TEXT_INPUT_TYPES.has((element as HTMLInputElement).type));

  if (!isTextInput) {
    return null;
  }

  // Threads' `Select` renders a searchable `type="text"` combobox and keeps
  // `name` on a separate hidden input, so requiring a name here is also what
  // keeps dropdown focus out of this event.
  return element?.name || null;
};

/**
 * Reports every focus of every text field on a form, so the funnel shows both
 * how far down a form visitors reach and which fields they return to.
 *
 * Repeat focuses are deliberately *not* collapsed: revisiting a field is the
 * hesitation signal this event exists to capture, so volume scales with how
 * much a visitor moves around the form rather than with the field count.
 *
 * Returns a handler for a delegated listener on the `<form>` rather than an
 * `onFocus` prop per field, so fields added to any layout are covered without
 * being wired up individually.
 */
export const useTrackTextInputFocus = ({
  getPayload,
}: {
  getPayload: () => Record<string, unknown>;
}) => {
  const track = useTrack();

  return (event: React.FocusEvent) => {
    const fieldName = getFocusedTextInputName(event.target);

    if (!fieldName) {
      return;
    }

    track({
      type: Events.FOCUSED,
      payload: {
        ...getPayload(),
        fieldName,
      },
    });
  };
};
